<template>
  <v-form @submit.prevent="submitForm" ref="formRef" v-model="formValid">
    <v-card class="px-5 py-3">
      <v-card-title class="d-flex justify-space-between align-center mb-3">
        <span>{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-row>
          <template v-for="field in visibleFields" :key="field?.value">
            <v-col v-if="field" cols="12" :md="field.col || 12" :sm="field.mdcol || 12">
              <component
                v-if="!field.isJsonArray"
                :is="field.component"
                v-model="form[field.value]"
                v-bind="getFieldProps(field)"
                :rules="field.required ? [v => !!v || 'Campo obligatorio'] : []"
              />
              <component
                v-else
                :is="field.component"
                v-model="form[field.value]"
                :fields="field.children"
                :summary="field.summary"
                :label="field.label"
                :form="form"
                @validation="onNestedValidation"
              />
            </v-col>
          </template>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn variant="text" @click="closeForm">Cancelar</v-btn>
        <v-btn type="submit" :disabled="!formValid || !nestedValid" color="secondary">Guardar</v-btn>
      </v-card-actions>
    </v-card>
  </v-form>
</template>

<script setup lang="ts">
import { ref, onMounted, watchEffect, toRaw } from 'vue';
import { useRoute } from 'vue-router';
import { configRegistry } from '@/lib/configRegistry';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useFloatFormStore } from '@/store/floatForm';
import type { FormField, FormProps } from '@/types/form';

const route = useRoute();
const moduleName = (route.meta.moduleName ?? route.name) as string;
const floatFormStore = useFloatFormStore();

const props = floatFormStore.props as FormProps;
const onCreate = props?.onCreate;
const formTitle = floatFormStore.formTitle;

const formFields = ref<FormField[]>([]);
const form = ref<Record<string, any>>({});
const formValid = ref(true);
const nestedValid = ref(true);
const formRef = ref();
const visibleFields = ref<FormField[]>([]);

watchEffect(() => {
  visibleFields.value = formFields.value
    .map((field) => {
      const val = form.value[field.dependsOn as any];
      const condition = field.dependsCondition;
      let conditionMet = true;

      if (field.dependsOn && field.dependsBehavior) {
        switch (condition?.condition) {
          case 'diferent':
            conditionMet = val !== undefined && val !== '' && val !== condition.value;
            break;
          case 'equals':
            conditionMet = val === condition.value;
            break;
          case 'includes':
            conditionMet = !val?.toLowerCase?.().includes(condition.value);
            break;
        }

        if (field.dependsBehavior === 'hidden' && !conditionMet) return null;
        if (field.dependsBehavior === 'disabled' && !conditionMet) {
          return { ...field, permission: 'disabled' };
        }
      }

      if (field.permission === 'hidden') return null;

      return field;
    })
    .filter((f): f is FormField => !!f);
});

const getFieldProps = (field: FormField) => {
  const props: Record<string, any> = {
    label: field.label,
    placeholder: field.placeholder ?? '',
    clearable: true,
    density: 'compact',
    type: field.type || 'text',
    prefix: field.prefix,
    suffix: field.suffix,
    'prepend-inner-icon': field.icon,
    disabled: field.permission === 'disabled',
    readonly: field.readonly,
    multiple: field.multiple || false,
    'item-title': field.itemText || 'label',
    'item-value': field.itemValue || 'value',
    'hide-no-data': true,
    range:field.range
  };

  if (field.options) props.items = field.options;

  if (field.fetchOptions && remoteOptionsRegistry[field.fetchOptions]) {
    props['onUpdate:search'] = async (val: string) => {
      const fetchFn = remoteOptionsRegistry[field.fetchOptions as any];
      let results = [];

      if (field.fetchParams) {
        const params: Record<string, any> = field.fetchParams(form.value) || {};

        if (typeof field.itemText === 'string' && val && val.length >= 1) {
          params[`filter[${field.itemText}]`] = val;
        }

        results = await fetchFn(params as any);
      } else {
        if (val && val.length >= 1) {
          results = await fetchFn(val);
        }
      }

      props['hide-no-data'] = !(val && val.length >= 1);

      field.options = results;
      props.items = results;
    };
  }

  return props;
};

const onNestedValidation = (valid: boolean) => {
  nestedValid.value = valid;
};

const resetForm = () => {
  formFields.value.forEach((f) => {
    if (f.isJsonArray && f.plainJson && f.children) {
      f.children.forEach((c) => (form.value[c.value] = ''));
    } else {
      form.value[f.value] = f.multiple ? [] : '';
    }
  });
};

const closeForm = () => {
  resetForm();
  floatFormStore.closeForm();
};

const flattenForm = (form: Record<string, any>, fields: FormField[]) => {
  const flatForm: Record<string, any> = {};
  for (const field of fields) {
    const val = form[field.value];

    if (typeof field.transform === 'function') {
      const result = field.transform(toRaw(val), form);
      if (typeof result === 'object' && !Array.isArray(result)) {
        Object.assign(flatForm, result);
      } else {
        flatForm[field.value] = result;
      }
      continue;
    }

    if (field.isJsonArray && field.children) {
      if(field.plainJson){
        for (const child of field.children) {
          flatForm[child.value] = val?.[child.value] ?? null;
        }
      }else{
        flatForm[field.value] = toRaw(val);
      }
    } else if (field.multiple && Array.isArray(val)) {
      flatForm[field.value] = val.map((v: any) =>
        typeof v === 'object' ? v[field.itemValue || 'value'] ?? v.id ?? null : v
      );
    } else if (typeof val === 'object' && val !== null) {
      flatForm[field.value] = val[field.itemValue || 'value'] ?? val.id ?? null;
    } else {
      flatForm[field.value] = val ?? null;
    }
  }

  return flatForm;
};

const submitForm = async () => {
  if (!formRef.value?.validate() || !nestedValid.value) return;

  try {
    const payload = flattenForm(form.value, formFields.value);
    if (onCreate) await onCreate(payload);
    else console.warn('[CreateForm] No se proporcionó función onCreate');
    closeForm();
  } catch (err) {
    console.error('[CreateForm] Error en onCreate', err);
  }
};

onMounted(async () => {
  const loader = configRegistry[moduleName];
  if (!loader) {
    console.error(`[CreateForm] Config para ${moduleName} no encontrada.`);
    return;
  }

  try {
    const mod = await loader();
    formFields.value = mod.CREATE_FORM || [];
    
    const initialValues = props.initialValues ?? {};

    for (const f of formFields.value) {
      const initial = initialValues[f.value];
      form.value[f.value] = initial ?? (f.multiple ? [] : '');
    }

  } catch (err) {
    console.error('[CreateForm] Error al cargar config:', err);
  }
});
</script>
